"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Gauge, ShieldCheck, Leaf, TrendingUp, Droplets, Zap, ArrowRight, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
const benefitsList = [{
  icon: TrendingUp,
  tag: "Eficiência",
  title: "Otimização de processos e dosagem precisa",
  description: "Controlo rigoroso do caudal e tamanho das gotas para reduzir o desperdício de matérias-primas e encurtar tempos de ciclo fabril.",
  metrics: "Até 40% de redução no consumo de químicos",
  points: ["Atomização homogénea e sem gotejamento", "Sistemas automatizados de controlo contínuo", "Redução direta do tempo de paragem de linha"]
}, {
  icon: ShieldCheck,
  tag: "Segurança",
  title: "Proteção operacional e fiabilidade mecânica",
  description: "Equipamentos concebidos para operar em condições industriais exigentes, minimizando a exposição humana a substâncias perigosas.",
  metrics: "Conformidade total com normas industriais",
  points: ["Operação estanque e limpeza CIP automática", "Materiais nobres resistentes a corrosão e abrasão", "Menor necessidade de intervenção direta do operador"]
}, {
  icon: Leaf,
  tag: "Sustentabilidade",
  title: "Conformidade ambiental e poupança hídrica",
  description: "Soluções de engenharia focadas no uso responsável da água e energia, diminuindo a pegada ecológica da sua infraestrutura.",
  metrics: "Economia substancial de água e energia",
  points: ["Redução comprovada do volume de efluentes", "Eficiência térmica em processos de arrefecimento", "Alinhamento com metas de descarbonização"]
}];
const highlights = [{
  icon: Droplets,
  value: "1937",
  label: "Inovação contínua Spraying Systems Co."
}, {
  icon: Zap,
  value: "1993",
  label: "Representação exclusiva em Portugal"
}, {
  icon: Gauge,
  value: "100k+",
  label: "Configurações de bicos industriais"
}];
export default function Benefits() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.15
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="technicalbenefits" data-nav="light" className="bg-background py-20 md:py-28 border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Header Block */}
        <div className="max-w-3xl mb-16 md:mb-20">
          <Badge variant="outline" className="mb-4 text-xs font-semibold uppercase tracking-wider">
            Vantagens técnicas
          </Badge>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-default font-bold text-foreground tracking-tight">
            Engenharia de precisão para resultados mensuráveis na sua linha.
          </h2>
          <p className="mt-5 text-lg text-muted-foreground leading-relaxed">
            Combinamos décadas de conhecimento de aplicação da Spraying Systems Co. com suporte técnico local em Portugal para elevar a produtividade, proteger as suas equipas e cumprir os requisitos ecológicos mais exigentes.
          </p>
        </div>

        {/* Benefits Cards Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {benefitsList.map((benefit, i) => {
          const Icon = benefit.icon;
          return <motion.div key={benefit.title} variants={itemVariants} data-index={i}>
                <Card className="h-full border border-border bg-card shadow-sm hover:shadow-md transition-[border-color,box-shadow] duration-300 flex flex-col justify-between">
                  <CardContent className="p-6 sm:p-8 flex flex-col h-full">
                    <div>
                      <div className="flex items-center justify-between gap-4 mb-6">
                        <div className="flex size-12 items-center justify-center rounded-lg bg-accent text-accent-foreground">
                          <Icon className="size-6" />
                        </div>
                        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                          {benefit.tag}
                        </span>
                      </div>

                      <h3 className="text-xl font-semibold text-card-foreground mb-3 font-default">
                        {benefit.title}
                      </h3>

                      <p className="text-muted-foreground text-sm leading-relaxed mb-6">
                        {benefit.description}
                      </p>
                    </div>

                    <div className="mt-auto pt-6 border-t border-border/60">
                      <div className="mb-4">
                        <span className="text-xs font-semibold text-secondary uppercase tracking-wider block mb-1">
                          Impacto direto
                        </span>
                        <span className="text-sm font-medium text-foreground">
                          {benefit.metrics}
                        </span>
                      </div>

                      <ul className="space-y-2.5">
                        {benefit.points.map((point, pointIndex) => <li key={pointIndex} data-index={pointIndex} className="flex items-start gap-2.5 text-xs sm:text-sm text-muted-foreground">
                            <CheckCircle2 className="size-4 text-chart-3 shrink-0 mt-0.5" />
                            <span>{point}</span>
                          </li>)}
                      </ul>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        {/* Supporting Banner & CTA */}
        <div className="mt-16 md:mt-20 rounded-2xl bg-muted/40 border border-border p-8 sm:p-10 lg:p-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-7 space-y-4">
              <span className="text-xs font-semibold text-secondary uppercase tracking-wider">
                Suporte de engenharia especializado
              </span>
              <h3 className="text-2xl sm:text-3xl font-bold text-foreground font-default">
                Precisa de avaliar o desempenho dos seus sistemas atuais?
              </h3>
              <p className="text-muted-foreground text-sm sm:text-base leading-relaxed">
                Os nossos técnicos realizam diagnósticos no local, simulações de pulverização e testes de desgaste para garantir a máxima eficiência em cada bico instalado.
              </p>
            </div>

            <div className="lg:col-span-5 flex flex-col sm:flex-row lg:flex-col xl:flex-row gap-4 lg:justify-end">
              <Button asChild size="lg" className="w-full sm:w-auto">
                <Link to="/ContactsPage">
                  Consulte-nos
                  <ArrowRight className="size-4 ml-1" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="w-full sm:w-auto">
                <Link to="/TechnicalSolutionsPage">
                  Soluções técnicas
                </Link>
              </Button>
            </div>
          </div>

          {/* Quick Metrics Strip */}
          <div className="mt-10 pt-8 border-t border-border/80 grid grid-cols-1 sm:grid-cols-3 gap-6">
            {highlights.map((item, idx) => {
            const ItemIcon = item.icon;
            return <div key={idx} data-index={idx} className="flex items-center gap-3.5">
                  <div className="flex size-10 items-center justify-center rounded-md bg-background border border-border text-primary shrink-0">
                    <ItemIcon className="size-5" />
                  </div>
                  <div>
                    <p className="text-lg font-bold text-foreground leading-tight font-default">
                      {item.value}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {item.label}
                    </p>
                  </div>
                </div>;
          })}
          </div>
        </div>
      </div>
    </section>;
}